import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Money, 
  Bank, 
  Receipt, 
  Download, 
  Calendar,
  ChartPie,
  ArrowRight
} from '@phosphor-icons/react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

export default function PaymentReport() {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Tarih aralığı state'leri - varsayılan olarak bu ayın başı ve sonu
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  });

  const PAYMENT_ICONS = {
    cash: Money,
    credit_card: CreditCard,
    bank_transfer: Bank,
    check: Receipt,
  };

  const PAYMENT_COLORS = {
    cash: 'bg-green-100 text-green-700 border-green-200',
    credit_card: 'bg-blue-100 text-blue-700 border-blue-200',
    bank_transfer: 'bg-purple-100 text-purple-700 border-purple-200',
    check: 'bg-orange-100 text-orange-700 border-orange-200',
  };

  useEffect(() => {
    fetchReport();
  }, [startDate, endDate]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/payment-summary?start_date=${startDate}&end_date=${endDate}`);
      setReport(res.data);
    } catch (error) {
      toast.error('Rapor yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${backendUrl}/api/reports/payment-summary/pdf?start_date=${startDate}&end_date=${endDate}`, '_blank');
    toast.success('PDF indiriliyor...');
  };
  
  // Hızlı tarih seçimi fonksiyonları
  const setThisMonth = () => {
    const now = new Date();
    const firstDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const lastDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    setStartDate(firstDay);
    setEndDate(lastDate);
  };
  
  const setLastMonth = () => {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const firstDay = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0).getDate();
    const lastDate = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    setStartDate(firstDay);
    setEndDate(lastDate);
  };
  
  const setThisYear = () => {
    const now = new Date();
    setStartDate(`${now.getFullYear()}-01-01`);
    setEndDate(`${now.getFullYear()}-12-31`);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1, 1);
    return date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="payment-report">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold">Ödeme Türü Raporu</h1>
              <p className="text-muted-foreground">Nakit, Kredi Kartı, Havale/EFT ve Tahsil Edilen Çekler</p>
            </div>
            <Button onClick={handleDownloadPDF} data-testid="download-payment-pdf">
              <Download className="w-4 h-4 mr-2" />
              PDF İndir
            </Button>
          </div>
          
          {/* Tarih Aralığı Seçimi */}
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Label>Başlangıç:</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-40"
                    data-testid="payment-start-date"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label>Bitiş:</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-40"
                    data-testid="payment-end-date"
                  />
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <Button variant="outline" size="sm" onClick={setThisMonth}>Bu Ay</Button>
                  <Button variant="outline" size="sm" onClick={setLastMonth}>Geçen Ay</Button>
                  <Button variant="outline" size="sm" onClick={setThisYear}>Bu Yıl</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : report && (
          <>
            {/* Genel Toplam */}
            <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <p className="text-slate-300 uppercase text-sm font-medium">
                      {report.date_label || `${formatDate(startDate)} - ${formatDate(endDate)}`} Toplam Ödeme
                    </p>
                    <p className="text-4xl font-bold mt-1">{formatCurrency(report.grand_total)}</p>
                  </div>
                  <div className="flex gap-6">
                    <div>
                      <p className="text-slate-400 text-sm">Giderler</p>
                      <p className="text-xl font-semibold text-red-400">{formatCurrency(report.expense_total)}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Stok Alımları</p>
                      <p className="text-xl font-semibold text-blue-400">{formatCurrency(report.stock_entry_total)}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Tahsil Edilen Çekler</p>
                      <p className="text-xl font-semibold text-orange-400">{formatCurrency(report.check_total || 0)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Ödeme Türü Kartları */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(report.payment_summary).map(([code, data]) => {
                const Icon = PAYMENT_ICONS[code] || Money;
                const colorClass = PAYMENT_COLORS[code] || PAYMENT_COLORS.cash;
                const percentage = report.grand_total > 0 ? (data.total / report.grand_total * 100).toFixed(1) : 0;
                
                return (
                  <Card key={code} className={`border-2 ${colorClass}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Icon size={24} weight="duotone" />
                            <span className="font-semibold">{data.label}</span>
                          </div>
                          <p className="text-2xl font-bold">{formatCurrency(data.total)}</p>
                          <p className="text-sm opacity-70">{data.count} işlem</p>
                        </div>
                        <div className="text-right">
                          <Badge variant="secondary" className="text-lg font-bold">
                            %{percentage}
                          </Badge>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="mt-3 h-2 bg-white/50 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-current opacity-60 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Detay Tabları */}
            <Tabs defaultValue="cash" className="space-y-4">
              <TabsList className="grid grid-cols-4 w-full">
                {Object.entries(report.payment_summary).map(([code, data]) => {
                  const Icon = PAYMENT_ICONS[code] || Money;
                  return (
                    <TabsTrigger key={code} value={code} className="flex items-center gap-2">
                      <Icon size={18} />
                      <span className="hidden sm:inline">{data.label}</span>
                      <Badge variant="outline" className="ml-1">{data.count}</Badge>
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {Object.entries(report.payment_summary).map(([code, data]) => (
                <TabsContent key={code} value={code}>
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>{data.label} Ödemeleri</span>
                        <span className="text-primary">{formatCurrency(data.total)}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {/* Özet */}
                      <div className="grid grid-cols-4 gap-4 mb-6">
                        <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                          <p className="text-xs text-red-600 uppercase">Giderler</p>
                          <p className="text-lg font-bold text-red-700">{formatCurrency(data.expenses)}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                          <p className="text-xs text-blue-600 uppercase">Stok Alımları</p>
                          <p className="text-lg font-bold text-blue-700">{formatCurrency(data.stock_entries)}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-orange-50 border border-orange-200">
                          <p className="text-xs text-orange-600 uppercase">Çekler</p>
                          <p className="text-lg font-bold text-orange-700">{formatCurrency(data.checks || 0)}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                          <p className="text-xs text-slate-600 uppercase">İşlem Sayısı</p>
                          <p className="text-lg font-bold text-slate-700">{data.count}</p>
                        </div>
                      </div>

                      {/* İşlem Listesi */}
                      {data.items.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <Receipt size={48} className="mx-auto mb-3 opacity-50" />
                          <p>Bu ödeme türünde işlem bulunamadı</p>
                        </div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Tarih</TableHead>
                              <TableHead>Tür</TableHead>
                              <TableHead>Açıklama</TableHead>
                              <TableHead className="text-right">Tutar</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {data.items.map((item, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="font-medium">{formatDate(item.date)}</TableCell>
                                <TableCell>
                                  <Badge variant={item.type === 'expense' ? 'destructive' : item.type === 'check' ? 'outline' : 'default'}>
                                    {item.type === 'expense' ? 'Gider' : item.type === 'check' ? 'Çek' : 'Stok'}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  {item.description}
                                  {item.quantity && (
                                    <span className="text-muted-foreground ml-2">
                                      ({item.quantity} {item.unit})
                                    </span>
                                  )}
                                  {item.status && (
                                    <Badge variant="secondary" className="ml-2 text-xs">
                                      {item.status === 'pending' ? 'Beklemede' : 
                                       item.status === 'cashed' ? 'Tahsil Edildi' : 
                                       item.status === 'bounced' ? 'Karşılıksız' : 'İptal'}
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell className={`text-right font-semibold ${item.amount < 0 ? 'text-green-600' : ''}`}>
                                  {formatCurrency(Math.abs(item.amount))}
                                  {item.amount < 0 && <span className="text-xs ml-1">(Alınan)</span>}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          </>
        )}
      </div>
    </Layout>
  );
}
